import React, { useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { HelperText } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';

import { ConnectHealthData, useRegistrationContext, ValidatedComponent } from '@luminate/luminate-native-components';
import { DateUtilities, Optional, PhoneType, RegistrationRequest } from '@luminate/luminate-ts-sdk';

import { useThemeContext, useAuth } from '../../contexts';
import { Dialog, Button } from '../common';
import { SCREEN_NAMES } from '../routes';

export const ConnectHealthDataScreen = () => {
  const {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    email,
    userName,
    password,
    guardianRequest,
    guardianFirstName,
    guardianLastName,
    guardianDateOfBirth,
    agreeToTerms,
    gender,
    setGender,
    last4,
    setLast4,
    phoneType,
    setPhoneType,
    phone,
    setPhone,
    addressOne,
    setAddressOne,
    addressTwo,
    setAddressTwo,
    city,
    setCity,
    state,
    setState,
    zip,
    setZip,
  } = useRegistrationContext();
  const { register, genders } = useAuth();
  const navigation = useNavigation();
  const { theme } = useThemeContext();

  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState(false);
  const [registrationErrorMessage, setRegistrationErrorMessage] = useState<string | undefined>(undefined);
  const connectHealthDataRef = useRef<ValidatedComponent>(null);

  const onBackToRegistration = () => {
    // @ts-ignore
    navigation.navigate(SCREEN_NAMES.REGISTER);
  };

  const onConnect = async () => {
    if (connectHealthDataRef.current?.isValid() ?? false) {
      setIsRegistering(true);
      try {
        const validatedLast4 = last4 && last4.trim().length > 0 ? last4 : undefined;
        await register({
          firstName,
          middleName,
          lastName,
          dateOfBirth: DateUtilities.fromShortDate(dateOfBirth),
          email,
          userName,
          password,
          guardianRequest,
          guardianFirstName,
          guardianLastName,
          guardianDateOfBirth: DateUtilities.fromShortDate(guardianDateOfBirth),
          gender,
          ssn4: validatedLast4,
          phoneType,
          phone,
          address1: addressOne,
          address2: addressTwo,
          state,
          city,
          zip,
          agreeToTerms,
        } as RegistrationRequest);
      } catch (ex) {
        setRegistrationError(true);
        // @ts-ignore
        setRegistrationErrorMessage(ex.message);
      } finally {
        setIsRegistering(false);
        if (window.location.href.includes('auth')) {
          //@ts-ignore
          navigation.navigate(SCREEN_NAMES.BROWSE);
        }
      }
    }
  };

  const onPhoneTypeChanged = (value: Optional<PhoneType>) => {
    setPhoneType(value ?? PhoneType.MOBILE);
  };

  return (
    <ScrollView style={{ backgroundColor: theme.colors.secondaryAccent }}>
      <Dialog
        contentStyle={{ zIndex: 1 }}
        style={{ marginVertical: 20 }}
        actions={
          <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'center' }}>
            <Button
              style={{ marginHorizontal: 5, marginVertical: 10 }}
              disabled={isRegistering}
              loading={isRegistering}
              onPress={onConnect}
            >
              Connect
            </Button>
            <Button style={{ marginHorizontal: 5, marginVertical: 10 }} onPress={onBackToRegistration}>
              Back
            </Button>
          </View>
        }
      >
        <View>
          <ConnectHealthData
            ref={connectHealthDataRef}
            genders={genders}
            gender={gender}
            onGenderChanged={setGender}
            address1={addressOne}
            onAddress1Changed={setAddressOne}
            address2={addressTwo}
            onAddress2Changed={setAddressTwo}
            city={city}
            onCityChanged={setCity}
            state={state}
            onStateChanged={setState}
            zip={zip}
            onZipChanged={setZip}
            phone={phone}
            onPhoneChanged={setPhone}
            phoneType={phoneType}
            onPhoneTypeChanged={onPhoneTypeChanged}
            last4={last4}
            onLast4Changed={setLast4}
          />
          <HelperText type={'error'} visible={registrationError}>
            {registrationErrorMessage ?? 'Unable to register account at this time.'}
          </HelperText>
        </View>
      </Dialog>
    </ScrollView>
  );
};
